import React, { ReactElement, useRef } from 'react';
import Popover, { IPopoverRef } from 'shared/components/Popover';
import { CustomSnoozeModal } from 'shared/components/CustomSnoozeModal';
import { useSnoozeProvider } from 'shared/providers/SnoozeProvider';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { ButtonComponent } from './ChangeClinicPopover';
import { Alert } from '@televet/kibble-ui/build/components';
import { Box } from '@televet/kibble-ui/build/chakra';
import { Text } from '@televet/kibble-ui/build/components/Text';

interface IUserProfilePopoverProps {
  anchor: ReactElement;
}

export const SnoozedInfoComponent = (): JSX.Element => {
  const { getSnoozeDescription } = useSnoozeProvider();

  return (
    <Box p={3}>
      <Alert
        variant="info"
        hideCloseButton
        title="You are snoozed"
        description={`Notifications paused until ${getSnoozeDescription()}`}
        iconProps={{
          name: 'bellCancel',
          variant: 'interactive',
        }}
      />
    </Box>
  );
};

const SnoozePopoverContent = ({ onClick, shownText }: { onClick: () => void; shownText: string }): JSX.Element => {
  return (
    <ButtonComponent onClick={onClick}>
      <Text size="sm">{shownText}</Text>
    </ButtonComponent>
  );
};

export const SnoozePopover = ({ anchor }: IUserProfilePopoverProps): JSX.Element => {
  const popoverRef = useRef<IPopoverRef>();
  const {
    snoozedUntil,
    isSnoozed,
    handleTimeClick,
    handleTomorrowClick,
    turnOffSnooze,
    isCustomSnoozeModalOpen,
    onCustomSnoozeModalOpen,
    onCustomSnoozeModalClose,
  } = useSnoozeProvider();
  const { isMobile } = useResolutionProvider();

  return (
    <Popover anchor={anchor} ref={popoverRef}>
      <>
        <CustomSnoozeModal isOpen={isCustomSnoozeModalOpen} onClose={onCustomSnoozeModalClose} />
        {snoozedUntil && isSnoozed && <SnoozedInfoComponent />}
        <Box px={3} py={1}>
          <Text fontWeight="bold">Do not disturb for...</Text>
        </Box>
        <SnoozePopoverContent
          onClick={(): void => {
            handleTimeClick(30);
            popoverRef.current?.hide();
          }}
          shownText="30 Minutes"
        />
        <SnoozePopoverContent
          onClick={(): void => {
            handleTimeClick(60);
            popoverRef.current?.hide();
          }}
          shownText="1 Hour"
        />
        {!isMobile && (
          <>
            <SnoozePopoverContent
              onClick={(): void => {
                handleTimeClick(120);
                popoverRef.current?.hide();
              }}
              shownText="2 Hours"
            />
            <SnoozePopoverContent
              onClick={(): void => {
                handleTimeClick(180);
                popoverRef.current?.hide();
              }}
              shownText="3 Hours"
            />
          </>
        )}
        <SnoozePopoverContent
          onClick={(): void => {
            handleTomorrowClick();
            popoverRef.current?.hide();
          }}
          shownText="Tomorrow at 8 am"
        />
        <SnoozePopoverContent
          onClick={(): void => {
            onCustomSnoozeModalOpen();
            popoverRef.current?.hide();
          }}
          shownText="Custom"
        />
        {isSnoozed && (
          <SnoozePopoverContent
            onClick={(): void => {
              turnOffSnooze();
              popoverRef.current?.hide();
            }}
            shownText="Turn off snooze"
          />
        )}
      </>
    </Popover>
  );
};
